import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Key, RefreshCw, Trash2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

const STATUSES = [
  { v: "new", c: "bg-[#0047FF] text-white" },
  { v: "contacted", c: "bg-[#FFB800] text-[#0B132B]" },
  { v: "qualified", c: "bg-[#0B132B] text-white" },
  { v: "won", c: "bg-green-600 text-white" },
  { v: "lost", c: "bg-gray-400 text-white" },
];

export default function AdminPage() {
  const { t } = useLang();
  const [key, setKey] = useState(localStorage.getItem("bbm_admin_key") || "");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/leads", { headers: { "x-admin-key": key } });
      setLeads(res.data);
      setAuthed(true);
      localStorage.setItem("bbm_admin_key", key);
    } catch (err) {
      setError(err?.response?.status === 401 ? "Invalid admin key" : "Failed to load");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status?status=${status}`, null, { headers: { "x-admin-key": key } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {/* */}
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`, { headers: { "x-admin-key": key } });
      setLeads((ls) => ls.filter((l) => l.id !== id));
    } catch {/* */}
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-page">
      <header className="bbm-glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#0B132B]" data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4 bbm-flip" />
            <span className="font-mono uppercase tracking-widest text-xs">Back to site</span>
          </Link>
          <span className="font-display text-lg text-[#0B132B]">BBM · Admin</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10">
          <p className="bbm-eyebrow mb-3">— Internal</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0B132B] mb-2">{t.admin.title}</h1>
          <p className="text-[#4B5563]">{t.admin.sub}</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-8">
          <label className="flex flex-col gap-2 max-w-md">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563]">{t.admin.key_label}</span>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Key className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                <input
                  type="password"
                  data-testid="admin-key-input"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder="bbm-admin-2025"
                  className="w-full ps-9 pe-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#0047FF]"
                />
              </div>
              <button
                onClick={load}
                disabled={loading || !key}
                data-testid="admin-load-btn"
                className="bbm-btn-primary !py-2.5"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : t.admin.load}
              </button>
            </div>
          </label>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-error">{error}</p>}
        </div>

        {authed && (
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="leads-table-container">
            {leads.length === 0 ? (
              <div className="p-12 text-center text-[#4B5563]" data-testid="leads-empty">{t.admin.empty}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr className="text-start font-mono text-[10px] uppercase tracking-widest text-[#4B5563]">
                      <th className="text-start p-4">Created</th>
                      <th className="text-start p-4">Name / Co.</th>
                      <th className="text-start p-4">Contact</th>
                      <th className="text-start p-4">Service</th>
                      <th className="text-start p-4">Status</th>
                      <th className="text-start p-4">Message</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`lead-row-${l.id}`}>
                        <td className="p-4 font-mono text-xs text-[#4B5563] whitespace-nowrap">
                          {new Date(l.created_at).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-[#0B132B]">{l.name}</div>
                          <div className="text-xs text-[#4B5563]">{l.company || "—"} · {l.country || "—"}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-[#0B132B]">{l.email}</div>
                          <div className="text-xs text-[#4B5563]">{l.phone || "—"}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-[#0B132B]">{l.service || "—"}</div>
                          <div className="text-xs text-[#4B5563]">{l.budget || "—"}</div>
                        </td>
                        <td className="p-4">
                          <select
                            value={l.status}
                            onChange={(e) => updateStatus(l.id, e.target.value)}
                            data-testid={`lead-status-${l.id}`}
                            className={`px-2 py-1 rounded text-xs font-mono uppercase tracking-widest border-0 ${STATUSES.find((s) => s.v === l.status)?.c || "bg-gray-200"}`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s.v} value={s.v} className="bg-white text-black">
                                {s.v}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-4 max-w-xs">
                          <div className="text-xs text-[#4B5563] line-clamp-3">{l.message}</div>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => remove(l.id)}
                            data-testid={`lead-delete-${l.id}`}
                            className="text-[#4B5563] hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
